import React, { useState, useEffect } from "react";
import { navigate } from "@reach/router";
import { AuthConsumer } from "../helpers/AuthContext";

// Post-signup email verification. The signup flow lands here with
// ?email=… prefilled; the user enters the 6-digit code we emailed. On
// success the server auto-logs them in, so we go straight to onboarding.
// A resend button re-issues the code.
const VerifyEmail = (props) => {
  const search =
    (props.location && props.location.search) ||
    (typeof window !== "undefined" ? window.location.search : "");
  const emailFromUrl =
    new URLSearchParams(search).get("email") || "";

  const [email, setEmail] = useState(emailFromUrl);
  const [otp, setOtp] = useState("");
  const [message, setMessage] = useState("");
  const [info, setInfo] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [resending, setResending] = useState(false);
  const [done, setDone] = useState(false);

  // On success, bounce to onboarding shortly (the server already logged
  // the user in via the returned session).
  useEffect(() => {
    if (!done) return undefined;
    const t = setTimeout(() => navigate("/onboarding"), 1200);
    return () => clearTimeout(t);
  }, [done]);

  const handleVerify = (verifyEmail) => {
    if (!email) {
      setMessage("Enter the email you signed up with.");
      return;
    }
    if (!/^\d{6}$/.test(otp.trim())) {
      setMessage("Enter the 6-digit code from your email.");
      return;
    }
    setIsSubmitting(true);
    setMessage("");
    setInfo("");
    verifyEmail({ email: email.trim().toLowerCase(), otp: otp.trim() })
      .then((data) => {
        setIsSubmitting(false);
        if (data && data.accessToken) {
          setDone(true);
        } else if (data && data.alreadyVerified) {
          // Nothing to do — send them to sign in.
          setInfo("This email is already verified. Redirecting to sign in…");
          setTimeout(() => navigate("/login"), 1200);
        } else {
          setDone(true);
        }
      })
      .catch((err) => {
        setIsSubmitting(false);
        const msg =
          err?.response?.data?.message ||
          "That code is invalid or has expired.";
        setMessage(msg);
      });
  };

  const handleResend = (resendVerification) => {
    if (!email) {
      setMessage("Enter your email first, then resend.");
      return;
    }
    setResending(true);
    setMessage("");
    setInfo("");
    resendVerification(email.trim().toLowerCase())
      .then(() => {
        setResending(false);
        setInfo("If that account still needs verifying, a new code is on its way.");
      })
      .catch(() => {
        setResending(false);
        setInfo("If that account still needs verifying, a new code is on its way.");
      });
  };

  return (
    <AuthConsumer>
      {({ verifyEmail, resendVerification }) => (
        <div className="theme-scientific auth-page">
          <div className="auth-shell auth-shell--solo">
            <section className="auth-form-panel">
              <header className="auth-form-panel__header">
                <div className="sci-eyebrow">ICMOBI / Verify your email</div>
                <h2 className="auth-form-panel__title">
                  {done ? "Email verified" : "Enter your code"}
                </h2>
                <p className="auth-form-panel__subtitle">
                  {done
                    ? "You're all set. Taking you to onboarding…"
                    : "We emailed a 6-digit code to confirm your address. Enter it below to activate your account."}
                </p>
              </header>

              {message && (
                <div className="sci-alert sci-alert--danger">
                  <div className="sci-alert__icon" />
                  <span>{message}</span>
                </div>
              )}
              {info && (
                <div className="sci-alert sci-alert--success">
                  <div className="sci-alert__icon" />
                  <span>{info}</span>
                </div>
              )}

              {done ? (
                <button
                  className="sci-btn sci-btn--primary sci-btn--full"
                  style={{ marginTop: 8 }}
                  onClick={() => navigate("/onboarding")}
                >
                  Continue →
                </button>
              ) : (
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    handleVerify(verifyEmail);
                  }}
                >
                  <div className="sci-field">
                    <label
                      className="sci-field__label sci-field__label--req"
                      htmlFor="ve-email"
                    >
                      Email
                    </label>
                    <input
                      id="ve-email"
                      type="email"
                      className="sci-input"
                      placeholder="you@institution.edu"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      autoComplete="email"
                    />
                  </div>

                  <div className="sci-field">
                    <label
                      className="sci-field__label sci-field__label--req"
                      htmlFor="ve-otp"
                    >
                      6-digit code
                    </label>
                    <input
                      id="ve-otp"
                      type="text"
                      inputMode="numeric"
                      maxLength={6}
                      className="sci-input"
                      placeholder="••••••"
                      value={otp}
                      onChange={(e) =>
                        setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))
                      }
                      autoComplete="one-time-code"
                    />
                  </div>

                  <button
                    type="submit"
                    className="sci-btn sci-btn--primary sci-btn--full"
                    disabled={isSubmitting}
                    style={{ marginTop: 8 }}
                  >
                    {isSubmitting ? "Verifying…" : "Verify email →"}
                  </button>

                  <button
                    type="button"
                    className="sci-btn sci-btn--ghost sci-btn--full"
                    style={{ marginTop: 10 }}
                    disabled={resending}
                    onClick={() => handleResend(resendVerification)}
                  >
                    {resending ? "Sending…" : "Resend code"}
                  </button>
                </form>
              )}

              <div className="auth-footer-link">
                <span>Already verified?</span>
                <a
                  href="/login"
                  onClick={(e) => {
                    e.preventDefault();
                    navigate("/login");
                  }}
                >
                  Sign in →
                </a>
              </div>
            </section>
          </div>
        </div>
      )}
    </AuthConsumer>
  );
};

export default VerifyEmail;
